// Resolve the bridge at call time. The SDK snapshots it at module import, so
// enablePullToRefresh can silently no-op when Android injects its bridge later.
export function setNativePullToRefresh(enabled: boolean): boolean {
	if (typeof window === 'undefined') return false;
	try {
		const android = window.WebToNativeInterface;
		if (typeof android?.enableSwipeRefresh === 'function') {
			android.enableSwipeRefresh(enabled);
			return true;
		}
		const ios = window.webkit?.messageHandlers?.webToNativeInterface;
		if (typeof ios?.postMessage === 'function') {
			ios.postMessage({ action: 'setPullToRefresh', enable: enabled });
			return true;
		}
	} catch {
		// The bridge may not be ready yet. Let the caller retry without breaking the page.
	}
	return false;
}

export function disableNativePullToRefresh() {
	let disabled = false;
	let retryTimer: ReturnType<typeof setTimeout> | undefined;
	let attempts = 0;

	const disable = () => {
		clearTimeout(retryTimer);
		attempts += 1;
		if (setNativePullToRefresh(false)) {
			disabled = true;
		} else if (attempts < 20) {
			retryTimer = setTimeout(disable, 500);
		}
	};
	const resume = () => {
		if (document.visibilityState === 'hidden') return;
		attempts = 0;
		disable();
	};

	disable();
	window.addEventListener('pageshow', resume);
	document.addEventListener('visibilitychange', resume);
	return () => {
		clearTimeout(retryTimer);
		window.removeEventListener('pageshow', resume);
		document.removeEventListener('visibilitychange', resume);
		if (disabled) setNativePullToRefresh(true);
	};
}

// Pages that scroll an inner element keep the document at the top, so the app's
// pull-to-refresh fires on every downward swipe. Keep it on only while `scroller`
// is scrolled to the top.
export function enableNativePullToRefreshAtTop(scroller: HTMLElement) {
	let enabled: boolean | null = null;

	const sync = () => {
		const atTop = scroller.scrollTop <= 0;
		if (atTop === enabled) return;
		// Without a bridge yet (Android injects it late) the next scroll retries.
		if (setNativePullToRefresh(atTop)) enabled = atTop;
	};

	sync();
	scroller.addEventListener('scroll', sync, { passive: true });
	return () => {
		scroller.removeEventListener('scroll', sync);
		if (enabled === false) setNativePullToRefresh(true);
	};
}
